/**
 * The typed face of `echo-chat-wasm` (web-plan.md F0): every rule both wire
 * ends enforce — markdown, the entity normal form, message splitting, album
 * geometry, the waveform, the avatar hash, contrast math, the emoji
 * vocabulary — is called here and implemented exactly once, in Rust.
 *
 * The raw exports speak JSON strings; this module parses them into the
 * generated dto types so nothing else ever sees a string boundary. Load is
 * explicit and awaited once at startup (`initWasm`), because every caller
 * after that wants synchronous answers — a composer that awaited a promise
 * per keystroke would be the wrong shape.
 */
import initChat, {
  album_height,
  avatar_color,
  avatar_gradient,
  avatar_initials,
  emoji_all,
  emoji_canonical,
  markdown_of,
  markdown_parse,
  markdown_parse_mentioning,
  mention_complete,
  mention_query,
  mention_suggest,
  normalize_entities,
  split_message,
  unpack_waveform,
} from "../wasm/chat/echo_chat_wasm";

import type { Attachment, Entity } from "../gen/dto";

let ready = false;
let initialization: Promise<void> | null = null;

/** Instantiates the wasm module. Everything else here throws until this has
 * resolved once — the app calls it before the first render. */
export async function initWasm(): Promise<void> {
  if (ready) return;
  initialization ??= initChat()
    .then(() => {
      ready = true;
    })
    .catch((error: unknown) => {
      initialization = null;
      throw error;
    });
  await initialization;
}

export interface ParsedMarkdown {
  text: string;
  entities: Entity[];
}

/** Somebody an `@username` can name (dioxus-plan.md D284): their id and the
 * handle they log in with. */
export type Handle = readonly [userId: number, username: string];

/** Composer input in, the server's normal form out (chat-api.md §1.2). Given
 * `people` — everybody the client knows — it also lays a `mention` over every
 * `@username` of somebody among them (D284): what a send and an edit's save go
 * through. Without, it is the reading that knows nobody, which is what a
 * length count wants. */
export function parseMarkdown(input: string, people?: readonly Handle[]): ParsedMarkdown {
  const json =
    people === undefined
      ? markdown_parse(input)
      : markdown_parse_mentioning(input, JSON.stringify(people));
  return JSON.parse(json) as ParsedMarkdown;
}

/** The `@word` the caret is in (D284), in UTF-16 units — the textarea's own
 * `selectionStart` unit: where the `@` is, one past what a pick replaces,
 * what was typed after the `@`, and whether a space already follows. */
export interface MentionQuery {
  at: number;
  end: number;
  needle: string;
  spaced: boolean;
}

export function mentionQuery(text: string, caret: number): MentionQuery | null {
  const json = mention_query(text, caret);
  return json === undefined ? null : (JSON.parse(json) as MentionQuery);
}

/** Somebody the `@` list may offer: id, handle, and the name they go by. */
export type MentionCandidate = readonly [userId: number, username: string, name: string];

/** Who `needle` could mean, best first (D284) — beginnings of the handle or
 * the name, then of a later word; `recent` (senders, newest first) leads
 * within each. */
export function suggestMentions(
  needle: string,
  people: readonly MentionCandidate[],
  recent: readonly number[],
): number[] {
  return JSON.parse(
    mention_suggest(needle, JSON.stringify(people), JSON.stringify(recent)),
  ) as number[];
}

/** The edit picking `username` makes, read against the box as it stands now:
 * replace `from..to` with `insert`, caret to `caret` — or null when the caret
 * has left the `@word`. */
export interface MentionCompletion {
  from: number;
  to: number;
  insert: string;
  caret: number;
}

export function completeMention(
  text: string,
  caret: number,
  username: string,
): MentionCompletion | null {
  const json = mention_complete(text, caret, username);
  return json === undefined ? null : (JSON.parse(json) as MentionCompletion);
}

/** What the composer opens when an edit begins (D20). */
export function toMarkdown(text: string, entities: Entity[]): string {
  return markdown_of(text, JSON.stringify(entities));
}

/** Validate + normalize under `Unknowns::Drop` — run before every send, so a
 * 400 from the server is a bug report and not an ordinary outcome. Throws
 * with the server's own sentence when the input cannot normalize. */
export function normalizeEntities(
  text: string,
  entities: Entity[],
  maxTextUnits: number,
): Entity[] {
  return JSON.parse(normalize_entities(text, JSON.stringify(entities), maxTextUnits)) as Entity[];
}

export interface SplitPart {
  text: string;
  entities: Entity[];
}

export interface SplitResult {
  parts: SplitPart[];
  truncated: boolean;
}

/** D131: an over-long message cut along paragraph → sentence → word → code
 * point, at most ten parts, entities cut with the text. */
export function splitMessage(text: string, entities: Entity[], limit: number): SplitResult {
  return JSON.parse(split_message(text, JSON.stringify(entities), limit)) as SplitResult;
}

/** One height for the whole album frame at `width` (chat-plan.md §6.3). */
export function albumHeight(attachments: Attachment[], width: number): number {
  return album_height(JSON.stringify(attachments), width);
}

/** The 100 five-bit buckets of a voice note's bar chart, from the
 * attachment's base64 `waveform`. */
export function unpackWaveform(waveformBase64: string): Uint8Array {
  const raw = atob(waveformBase64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return unpack_waveform(bytes);
}

/** «Иван Петров» → «ИП» (D73). */
export const avatarInitials = (name: string): string => avatar_initials(name);

/** The person's flat colour — a `var(--accents-…)` token. */
export const avatarColor = (name: string, registeredAtS: number): string =>
  avatar_color(name, BigInt(registeredAtS));

/** The circle's `background-image` gradient (D73). */
export const avatarGradient = (name: string, registeredAtS: number): string =>
  avatar_gradient(name, BigInt(registeredAtS));

/** Canonical spelling of a reaction, or null for a value outside the closed
 * vocabulary — shown as a neutral chip, never sent back (chat-api.md §7). */
export function emojiCanonical(input: string): string | null {
  return emoji_canonical(input) ?? null;
}

let vocabulary: string[] | null = null;

/** The whole closed reaction vocabulary (D152) — what the picker draws. */
export function emojiVocabulary(): string[] {
  vocabulary ??= JSON.parse(emoji_all()) as string[];
  return vocabulary;
}
